// A trust configuration's public fields as a form (provider.ts): the same
// fields, the same hints, on the workspace's own sign-in and in a child's
// blueprint, where they are pushed down as the managed provider. The
// set-up's third step passes `clients` and `more`: one screen with what the
// provider shows — the issuer and its client id, both only where it has two
// clients — and the claims, the scopes and where people may sign in, each
// with a working default from the provider chosen, under *More options*,
// closed (01-the-console.md § Settings: the Sign-in tab, § conventions: a
// form). Without them every field is drawn, as a blueprint's form draws it.

import { Field, TextInput } from "../../ui/components.tsx";
import { nowhere, type ProviderDraft } from "./provider.ts";

/**
 * Clients is which client ids a provider asks for: `one`, the one
 * registration; `two`, one for the browser and one for the terminal; or
 * `either`, one, with the two per surface under *More options* for a
 * provider that wants them.
 */
export type Clients = "one" | "two" | "either";

export function ProviderFields({
  d,
  set,
  autoFocus,
  checked = "Saving checks it against the provider.",
  clients,
  more = false,
}: {
  d: ProviderDraft;
  set: (patch: Partial<ProviderDraft>) => void;
  autoFocus?: boolean;
  /** When the issuer is checked against the provider: at save, unless said otherwise. */
  checked?: string;
  /** Which client ids to ask for; every one where omitted. */
  clients?: Clients;
  /** The claims, the scopes and the surfaces under *More options*, closed. */
  more?: boolean;
}) {
  const text = (k: keyof ProviderDraft, label: string, hint?: string, placeholder?: string) => (
    <Field label={label} hint={hint}>
      <TextInput
        value={String(d[k])}
        onChange={(v) => {
          set({ [k]: v });
        }}
        placeholder={placeholder}
        mono
      />
    </Field>
  );
  const box = (k: "browser" | "terminal" | "desktop", label: string) => (
    <label className="checkbox">
      <input
        type="checkbox"
        checked={d[k]}
        onChange={(e) => {
          set({ [k]: e.target.checked });
        }}
      />
      {label}
    </label>
  );
  const perSurface = (
    <div className="two">
      {text(
        "browserClientId",
        "Browser client id",
        "Where the provider wants one per surface (Okta, Google).",
      )}
      {text("terminalClientId", "Terminal client id")}
    </div>
  );
  const claims = (
    <>
      <div className="two">
        {text(
          "orgClaim",
          "Organisation claim",
          "Optional: tid on Entra, hd on Google, https://slack.com/team_id on Slack.",
        )}
        {text("orgValue", "Required value")}
      </div>
      <div className="two">
        {text("roleClaim", "Role claim", "roles on Entra; the groups claim on Okta; hd or none on Google.")}
        {text(
          "handleClaim",
          "Handle claim",
          "email, or empty: what a person may be added by, read only where the token marks it verified.",
        )}
      </div>
      <div className="two">
        {text("subjectClaim", "Identity claim", "oid on Entra; sub when empty.")}
        {text("displayClaim", "Display claim", "name when empty.")}
      </div>
      {text("scopes", "Scopes", "Space-separated; openid profile email offline_access when empty.")}
      <div className="field">
        <span className="label">Where people may sign in</span>
        <span className="row">
          {box("browser", "the browser")}
          {box("terminal", "the terminal")}
          {box("desktop", "desktop AI clients")}
        </span>
        {nowhere(d) && <span className="hint">Pick at least one.</span>}
      </div>
    </>
  );
  return (
    <>
      <Field
        label="Issuer URL"
        hint={`Exactly as the provider's discovery states it; never a shared 'common' endpoint. ${checked}`}
      >
        <TextInput
          value={d.issuer}
          onChange={(v) => {
            set({ issuer: v });
          }}
          placeholder="https://login.microsoftonline.com/<directory id>/v2.0"
          mono
          required
          autoFocus={autoFocus}
        />
      </Field>
      {clients !== "two" &&
        text("clientId", "Client id", "The one registration, where the provider needs only one.")}
      {(clients === undefined || clients === "two") && perSurface}
      {more ? (
        <details className="more-options">
          <summary className="small">More options</summary>
          <div className="stack">
            {clients === "either" && perSurface}
            {claims}
          </div>
        </details>
      ) : (
        claims
      )}
    </>
  );
}
